import React, { useEffect, useState, useMemo } from 'react';

import { getZonedDate } from '@eika-infrastruktur/date';
import { useViewType, ViewType } from '@eika/hooks';
import { WarningLabel } from '@eika/label';

import { useConfig } from '../../../defaultConfig';
import { paymentFromSelector, useAccountFreeWithdrawals, useAccounts } from '../../../fetchHooks/useAccount';
import usePaymentLimits from '../../../fetchHooks/usePaymentLimits';
import type { EFakturaId } from '../../../generated-types';
import { usePaymentAccountContext } from '../../../hooks/useAccountState';
import { useFormContext } from '../../../hooks/useForm';
import useTranslation from '../../../hooks/useTranslation';
import filterInvalidAccounts from '../../../utils/filterInvalidAccounts';
import { AccountFreeWithdrawalLabel } from '../../shared/AccountFreeWithdrawalLabel';
import { PaymentError } from '../Payment/form/validate';
import { EFakturaDesktopFields, EFakturaMobileFields, FieldProps } from './fields';
import { PaymentAction, PaymentActionType } from './form/actions';
import { EfakturaState } from './form/state';

interface Props {
    locked?: boolean;
    kidLoading?: boolean;
    eFakturaId?: EFakturaId;
    loadingEfakturaId?: boolean;
    useDesktopPlaceholder?: boolean;
}

const today = getZonedDate();
const EFakturaFields: React.FC<Props> = ({
    locked = false,
    kidLoading = false,
    eFakturaId,
    loadingEfakturaId,
    useDesktopPlaceholder = true,
}) => {
    const translated = useTranslation();

    const { state, errors, handleChange } = useFormContext<EfakturaState, PaymentAction, PaymentError>();

    const { featureToggles } = useConfig();
    const [hasChangedDate, setChangedDate] = useState(false);
    const { data: allPaymentAccounts, ...accountState } = useAccounts({ select: paymentFromSelector });
    const { data: allAccounts } = useAccounts();
    const invalidAccounts = filterInvalidAccounts({ allAccounts, validAccounts: allPaymentAccounts });
    const { data: paymentLimits } = usePaymentLimits();

    const { targetFromAccount } = usePaymentAccountContext();
    const { data: freeWithdrawals, showFreewithdrawal } = useAccountFreeWithdrawals(
        targetFromAccount,
        featureToggles.enableGettingAccountWithdrawalsInPayment,
    );

    useEffect(() => {
        if (state.paymentDate && state.paymentDate < today) {
            handleChange({
                type: PaymentActionType.PAYMENT_DATE,
                payload: today,
            });
            setChangedDate(true);
        }
    }, [handleChange, state.paymentDate]);

    const emptyFieldWarningLabel =
        accountState.isFetched && allPaymentAccounts?.length === 0 && !errors.accountId ? (
            <WarningLabel text={translated.eFaktura.noValidFromAccounts} />
        ) : undefined;

    const waitingForReceiver = locked && !state.toAccount;

    const showMinAmount =
        (state.minimumAmount || 0) > 0 &&
        ((state.minimumAmount || 0) < state.totalAmount || (state.amount || 0) < state.totalAmount);

    const bottomLabel = useMemo(() => {
        if (showFreewithdrawal) {
            return <AccountFreeWithdrawalLabel freeWithdrawals={freeWithdrawals} />;
        }
        return undefined;
    }, [showFreewithdrawal, freeWithdrawals]);

    const viewType = useViewType();
    const isMobile = viewType === ViewType.mobile;
    const messageAllowed = !state.toAccount?.kidRequired;
    const fieldProps: FieldProps = {
        showMinAmount,
        locked,
        accounts: allAccounts,
        invalidAccounts,
        accountState,
        waitingForReceiver,
        kidLoading,
        eFaktura: {
            id: eFakturaId,
            loading: loadingEfakturaId,
        },
        date: {
            minDate: today,
            hasChangedDate,
            setChangedDate,
        },
        bottomLabel,
        emptyFieldWarningLabel,
        useDesktopPlaceholder,
        paymentLimits,
        messageAllowed,
    };
    return isMobile ? <EFakturaMobileFields {...fieldProps} /> : <EFakturaDesktopFields {...fieldProps} />;
};

export default EFakturaFields;
